<template>
  <div v-if="isFetched">
    <campaign-info
      :startDateText="startDateText"
      :endDateText="endDateText"
      :campaignRepeatsText="campaignRepeatsText"
      :campaign="campaign"
      :number="number"
      :isNew="isNew"
      :status="status"
    />

    <div class="card-spacing" />

    <campaign-coupon :campaignId="campaignId" :number-of-coupons="campaign.coupons.length" />

    <div class="card-spacing" />

    <campaign-notification
      :title.sync="campaign.notificationTitle"
      :description.sync="campaign.notificationDescription"
      :delay.sync="campaign.delay"

      :isSavedNotification.sync="isSavedNotification"
      :isNew="isNew"
      @update-campaign="save"
    />
    <div class="card-spacing" />
    <campaign-date-time
      :startDate.sync="campaign.startDate"
      :endDate.sync="campaign.endDate"
      :startTime.sync="campaign.startTime"
      :endTime.sync="campaign.endTime"
      :daysActive.sync="campaign.daysActive"
      :campaignId="campaignId"

      :startDateText="startDateText"
      :endDateText="endDateText"
      :campaignRepeatsText="campaignRepeatsText"

      :isSavedDateTime.sync="isSavedDateTime"
      :isNew="isNew"
      @update-campaign="save"
    />

    <div class="card-spacing" />

    <div class="card mb1">
      <campaign-status
        v-if="visible"
        :key="campaign.id + 'campaign-starts-counter'"
        :campaign="updateWeekdays(campaign)"
        @status-update="status = $event"
      />

      <button
        v-if="isNew"
        class="box primary block mt3"
        @click="startCampaign"
        name="startcampaign"
        :disabled="!isSavedNotification || !isSavedDateTime"
      >
        {{ $t('cms.campaign.StartCampaignButton') }}
      </button>

      <delete
        class="my2"
        v-else
        @delete="deleteCampaign"
        :text="`${$t('cms.campaign.DeleteCampaignTitle')} ${number + 1}/7`"
        :subheader="$t('cms.campaign.DeleteCampaignText')"
      />
    </div>
  </div>
</template>

<script>
import omit from 'lodash/omit';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import store from '../../store';
import CampaignStatus from './CampaignStatus.vue';
import Delete from '../Delete.vue';
import { isIdGenerated, isIdNew } from '../../utils/id_utils';
import { saveCoupon } from '@/utils/save_utils';
import { captureException } from '../../plugins/debugging';
import MODELS from '@/constants/models';
import CampaignInfo from './components/CampaignInfo.vue';
import CampaignCoupon from './components/CampaignCoupon.vue';
import CampaignNotification from './components/CampaignNotification.vue';
import CampaignDateTime from './components/CampaignDateTime.vue';

export default {
  name: 'campaign',

  components: {
    CampaignStatus,
    Delete,
    CampaignInfo,
    CampaignCoupon,
    CampaignNotification,
    CampaignDateTime,
  },

  props: {
    campaignId: { type: String, required: true },
    visible: { type: Boolean, required: true },
    number: { type: Number, required: true },
  },

  beforeDestroy() {
    const campaignWithPreviousCoupons = this.campaign;
    campaignWithPreviousCoupons.coupons = store.getCampaign(this.campaign.id).coupons;
    store.storeCampaign(campaignWithPreviousCoupons);
  },

  data() {
    return {
      isFetched: false,
      isSavedNotification: false,
      isSavedDateTime: false,
      campaign: MODELS.campaign,
      status: null,
      isSaving: false,
    };
  },

  computed: {
    saveText() {
      if (this.isSaving) {
        return `${this.$t('cms.campaign.Saving')}...`;
      }

      return this.$t('cms.campaign.SaveChanges');
    },

    hasCoupons() {
      return this.campaign.coupons.length !== 0;
    },

    isNew() {
      return isIdNew(this.campaignId);
    },

    startDateText() {
      return this.dateStringToText(this.campaign.startDate);
    },

    endDateText() {
      return this.dateStringToText(this.campaign.endDate);
    },

    campaignRepeatsText() {
      if (this.campaign.daysActive.length < 1) {
        return '-';
      }

      if (this.campaign.daysActive.length === 7) {
        return `${this.$t('cms.campaign.Every')} ${this.$t('cms.campaign.Day')}`;
      }

      // Make sure the weekdays are always in the right order
      let sortedDays = this.$CONSTANTS.weekDays.filter(
        weekday => this.campaign.daysActive.includes(weekday.number),
      );

      // If there are to many days to fit on one line, make the names shorter
      // Ad create sentence to place on button instead of array
      let sortedDayNames = '';

      if (sortedDays.length > 2) {
        sortedDays = sortedDays.map(day => `${day.name.substr(0, 3)}.`);
        sortedDayNames = `${this.$t('cms.campaign.Every')} ${sortedDays.join(' ')}`;
      } else {
        sortedDays = sortedDays.map(day => day.name);
        sortedDayNames = `${this.$t('cms.campaign.Every')} ${sortedDays.join(', ')}`;
      }

      return sortedDayNames;
    },
  },

  async created() {
    if (isIdGenerated(this.campaignId) || store.containsCampaignId(this.campaignId)) {
      this.campaign = store.getCampaign(this.campaignId);
      this.isFetched = true; // set isFetched=true to render components in this case
    } else {
      await this.fetchCampaign();
    }

    if (navigator.userAgent.includes('Selenium')) {
      this.campaign.daysActive = this.$CONSTANTS.weekDays.map(weekday => weekday.number);
    }
  },

  methods: {
    async fetchCampaign() {
      const { data, errors } = await this
        .$query(this.$queries.campaign, { id: this.campaignId });
      this.handleOptionalErrors(errors);
      if (data.campaign) {
        this.isFetched = true;
        this.campaign = cloneDeep(data.campaign);
        store.storeCampaign(this.campaign);
      }
    },

    updateWeekdays(campaign) {
      return {
        ...campaign,
        daysActive: campaign.daysActive.map(day => (day === 6 ? 0 : day + 1)),
      };
    },

    dateStringToText(date) {
      if (date) {
        return new Date(date).toLocaleDateString('en-GB', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      }
      return null;
    },

    async deleteCampaign() {
      const START_OF_CAMPAIGN_Y_POSITION = 500;
      window.scroll(0, START_OF_CAMPAIGN_Y_POSITION);

      // Save to server if it exists there
      if (!isIdGenerated(this.campaignId)) {
        this.campaign.isDeleted = true;
        this.save();
      }

      this.campaign = cloneDeep(store.createNewCampaign());
      this.$emit('update:campaignId', this.campaign.id);
    },

    async save() {
      this.isSaving = true;
      if (this.campaign.coupons.length === 0) {
        this.handleOptionalErrors([
          this.$t('cms.campaign.invalidsave.CouponsRequired'),
        ]);
        return null;
      }

      const campaignInput = omit(this.campaign, 'coupons');
      let mutation = this.$mutations.updateCampaign;
      if (isIdGenerated(campaignInput.id)) {
        delete campaignInput.id;
        campaignInput.organization = store.state.organization.id;
        mutation = this.$mutations.createCampaign;
      }

      const { data, errors } = await this.$mutate(mutation, { campaignInput });

      this.isSaving = false;
      this.handleOptionalErrors(errors);
      return data;
    },

    async startCampaign() {
      if (this.isSavedNotification && this.isSavedDateTime) {
        const campaign = get(await this.save(), 'createCampaign.campaign');

        if (!campaign || !this.campaignId) {
          // shouldnt happen:
          captureException(Error(`"this.save()" Returned campaign: ${campaign}, must not be null and contain Id`));
          location.reload(); // Reload client so we have the right state from server. Just to be sure.
        }

        this.campaign = cloneDeep({ ...campaign, coupons: this.campaign.coupons });

        await Promise.all(this.campaign.coupons.map(coupon => saveCoupon(coupon, this.campaign.id, true)));
        this.$emit('update:campaignId', this.campaign.id);
      }
    },
  },
};
</script>

<style lang="scss">
.grow2 {
  flex-grow: 2;
}

.day_checkbox_row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: $height_box;
  padding-right: $base_spacing * 2;
  padding-left: $base_spacing * 2;
  border-top: $color_border_input solid 1px;
}

#weekday_collapse {
  @supports (-webkit-overflow-scrolling: touch) {
    // IOS ONLY, To fix weird border sticking out
    margin-left: 2px;
    margin-right: 2px;
  }

  display: block;
  border: black solid 1px;
  border-top: none;
  border-bottom-left-radius: $border_radius;
  border-bottom-right-radius: $border_radius;
}

.weekday_button_uncollapsed {
  margin-bottom: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.weekday_checkbox {
  display: none;
}

.length-count {
  margin-top: -$base_spacing * 5;
  padding-bottom: 20px;
  padding-right: $base_spacing * 2;
  text-align: end;
  z-index: 2;
}
</style>
